/* eslint no-label-var: "off" */
import { createBrowserHistory } from 'history';
import React, { useEffect, useMemo, useState } from 'react';
/** Components */
import Layout from './layout';
import LayoutWide from './layout/layoutWide';
import LayoutAuth from './layout/auth';
import LayoutAuthUp from './layout/authUp';
import LayoutAuthReverse from './layout/authReverse';
import LayoutAuthWide from './layout/authWide';

import EventEmitter from 'react-native-eventemitter';
import LayoutAuthWideFooter from './layout/authWideFooter';
import Report from './views/report';
import { useTranslation, withTranslation, Trans } from 'react-i18next';
import './i18n';
import { useSelector, useDispatch } from 'react-redux';
/** Styles in pures CSS */
import './assets/font-awesome/css/all.css';
import './assets/styles/index.css';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect
} from 'react-router-dom';
import UrlChangeTracker from './analytics/components/UrlChangeTracker';
import { ganttAPI } from './utils/customGanttPlugin';
import { socket } from './services/socket.service';
import * as projectActions from './redux/slices/projectSlice';
import { propertiesActions } from './redux/actions/propertiesAction';
import { sectorService } from './services/sector.service';
import { permissionService } from './services/permission.service';
import { compareValues } from './utils';
import { message } from 'antd';
import { Modal, Row } from 'antd';
import { base } from './services/base';
import { userPropertiesAnalytics } from './analytics';
import { propertiesService } from './services/properties.service';
import { AMPLITUDE_SERVICE, TOTANGO_SERVICE } from './analytics/constants';
import {
  setViews,
  setGanttLoading,
  setInitialGanttState
} from './redux/slices/ganttSlice';
import { defaultViewService } from './services/defaultViews.service';
import { setupDynamicHubSpotStyles } from './utils/hubSpotStyles';
import { useWarningForLowerEnvs } from './hooks/useWarningForLowerEnvs';
import { useCheckActualSector } from './hooks/useCheckActualSector';
import { userService } from './services';
import BaseLoader from './layout/loader/base';
import { companyActions } from './redux/actions/companyActions';
import { useLogout } from './hooks/useLogout';
import { getSessionTokenData, getSignedUser } from './utils/userUtils';
import * as Sentry from '@sentry/react';
import SSOProcoreWebview from './views/login/SSOProcoreWebview';
import {
  ACCESS_GRANTED,
  INVALID_LINK,
  LOADING_ACCESS,
  NO_MODULE_ACCESS,
  NO_ORGANIZATION_ACCESS,
  NO_SCHEDULE_ACCESS,
  NOT_FOUND
} from './constants/access';
import { useInitializeSession } from './hooks/useInitializeSession';
import InvalidLinkLayout from './layout/invalidLink';
import { exactRouteList, partialRouteList } from './router/routes';
import { useCheckUserAndLocation } from './hooks/useCheckUserAndLocation';
import { cleanColumnsIssues } from './views/ganttContainer/gantt/viewUtils';
import { useLoadFeatureFlags } from './hooks/useLoadFeatureFlags';
import { useLoadUserSessionData } from './hooks/useLoadUserSessionData';
import { TimerManagerSingleton } from './utils/timerManager';
import './App.css';

const SentryRoute = Sentry.withSentryRouting(Route);

const signed = localStorage.getItem('signed');
const DEFAULT_MONTH_ZOOM_LABEL = JSON.stringify('month');

function LayoutRender({ children }) {
  const signed = localStorage.getItem('signed');
  const LayoutComponent = signed ? Layout : LayoutAuth;
  return <LayoutComponent>{children}</LayoutComponent>;
}

const DynamicLayout = () => {
  useCheckUserAndLocation();
  const { status } = useInitializeSession();
  const component = useMemo(() => {
    const componentByStatus = {
      [LOADING_ACCESS]: null,
      [NOT_FOUND]: <InvalidLinkLayout type={NOT_FOUND} />,
      [NO_ORGANIZATION_ACCESS]: (
        <InvalidLinkLayout type={NO_ORGANIZATION_ACCESS} />
      ),
      [NO_SCHEDULE_ACCESS]: <InvalidLinkLayout type={NO_SCHEDULE_ACCESS} />,
      [NO_MODULE_ACCESS]: (
        <Layout unauthorized={true}>
          <InvalidLinkLayout type={NO_MODULE_ACCESS} />
        </Layout>
      ),
      [ACCESS_GRANTED]: <Layout unauthorized={false} />
    };

    return componentByStatus[status];
  }, [status]);

  return component;
};

function isDefaultViewEmpty(defaultView) {
  return Array.isArray(defaultView);
}

function filterDefaultView(defaultView) {
  const DEFAULT_VIEW_ID = defaultView.defaultView.id;
  const DEFAULT_SCHEDULE_VIEW = defaultView.scheduleViews.find(
    (view) => view.id === DEFAULT_VIEW_ID
  );
  return DEFAULT_SCHEDULE_VIEW;
}

function App(props) {
  useLoadUserSessionData();
  const { t } = props;
  window.translateObject = t;
  const projectState = useSelector((state) => state.projectState);
  const dispatch = useDispatch();
  useLogout();
  useWarningForLowerEnvs();
  const { loading: loadingActualSector } = useCheckActualSector(userService);
  const { loading: loadingFeatureFlags } = useLoadFeatureFlags();

  useEffect(() => {
    EventEmitter.on('loadScheduleView', loadScheduleView);

    return () => {
      EventEmitter.removeListener('loadScheduleView', loadScheduleView);
    };
  }, []);

  const loadScheduleView = async (_) => {
    resetDefaultViewTemplateView();
    setGanttToLoading();
    const { allSectors } = projectState;
    if (allSectors) {
      const sector = await sectorService.show(projectState.sectorSelected);
      if (sector && sector.sector) {
        dispatch(setInitialGanttState());
        window.localStorage.setItem('scale', DEFAULT_MONTH_ZOOM_LABEL);
        const currentSector = sector.sector;
        const copy_sector = { ...currentSector };
        copy_sector.cncs = [];
        copy_sector.constraints = [];
        copy_sector.weekcommitments = [];
        sessionStorage.setItem('currentSector', JSON.stringify(copy_sector));
        ganttAPI.updateFormatter();
        const defaultView = await defaultViewService.getDefaultView(
          projectState.sectorSelected
        );
        if (isDefaultViewEmpty(defaultView)) {
          resetDefaultViewTemplateView();
          return;
        }
        const defaultViewTemplate = filterDefaultView(defaultView);
        defaultViewTemplate.columns = cleanColumnsIssues(
          defaultViewTemplate.columns
        );
        if (defaultViewTemplate) {
          dispatch(
            setViews({
              ...defaultViewTemplate
            })
          );
        }
      }
    }
  };

  useEffect(() => {
    loadScheduleView();
  }, [projectState.sectorSelected]);

  function resetDefaultViewTemplateView() {
    dispatch(setViews({}));
  }

  function setGanttToLoading() {
    dispatch(setGanttLoading(true));
  }
  useEffect(() => {
    if (projectState.allProjects) {
      const project = projectState.allProjects.find(
        (e) => e.id == projectState.projectSelected
      );
      if (project) {
        const copy_project = { ...project };
        copy_project.sectors = [];
        sessionStorage.setItem('currentProject', JSON.stringify(copy_project));
      }
    }
  }, [projectState.projectSelected]);

  async function getSectors() {
    const resp = await sectorService.index();
    return resp ? resp.sectors : false;
  }

  useEffect(() => {
    setPermitionRefresh();
  }, []);

  useEffect(() => {
    const companySelected = sessionStorage.getItem('company');
    if (!companySelected) return;

    const currentUser = getSignedUser();
    if (!currentUser) return;

    handleUserPropertiesAnalytics(currentUser, companySelected);
    window.Appcues.identify(currentUser.id, {
      email: currentUser.email,
      firstName: currentUser.name,
      createdAt: currentUser.createdAt,
      role: currentUser.role
    });
    window.Appcues.page();
  });

  const setPermitionRefresh = async () => {
    const permissions = await permissionService.getPermissionsByUsers();
    if (!permissions.permissionTable) return;
    sessionStorage.setItem(
      'permissiontable',
      JSON.stringify(permissions.permissionTable)
    );
  };

  const handleUserPropertiesAnalytics = async (user, companyData) => {
    if (!companyData) return;

    const company = JSON.parse(companyData);
    if (!company?.id) return;
    const currentUser = {
      ...user,
      company: company?.name
    };
    userPropertiesAnalytics(currentUser, AMPLITUDE_SERVICE);
    userPropertiesAnalytics(currentUser, TOTANGO_SERVICE);
  };

  const refreshSectors = async (reload = true) => {
    const timerManager = TimerManagerSingleton.getInstance();

    if (reload)
      message.info(
        'Alguien ha actualizado el criterio de horas por dia, actualizando...'
      );
    const sectors = await getSectors();

    if (sectors) {
      const filterSectors = sectors.filter(
        (e) => e.projectId === projectState.projectSelected && e.status === true
      );
      filterSectors.sort(compareValues('order'));

      /** update state sectors */
      dispatch(projectActions.setAllSectors(filterSectors));

      if (reload) {
        const refreshCallback = () => {
          window.location.reload();
        };
        timerManager.registerAutoTimeout(
          refreshCallback,
          1000,
          'refreshSectors'
        );
      }
    }
  };

  useEffect(() => {
    socket.on('hours_updated_' + projectState.sectorSelected, refreshSectors);
    return () => {
      socket.off(
        'hours_updated_' + projectState.sectorSelected,
        refreshSectors
      );
    };
  }, [projectState.sectorSelected]);

  useEffect(() => {
    const cleanup = setupDynamicHubSpotStyles(window.location.pathname);
    return () => cleanup();
  }, [window.location.pathname]);

  const history = createBrowserHistory();

  return loadingActualSector || loadingFeatureFlags ? null : (
    <Router history={history}>
      <UrlChangeTracker />
      <Switch>
        <SentryRoute path="/reportlookahead/week/allNewTab">
          <Layout withoutLayout={true} />
        </SentryRoute>
        <SentryRoute path="/register">
          <LayoutAuth />
        </SentryRoute>
        <SentryRoute path={['/login']}>
          <LayoutAuthReverse />
        </SentryRoute>
        <SentryRoute path="/sso/login">
          <SSOProcoreWebview />
        </SentryRoute>
        <SentryRoute path={['/forgot']}>
          <LayoutAuthReverse />
        </SentryRoute>
        <SentryRoute path={['/signup', '/signupconfirmation']}>
          <LayoutAuthReverse />
        </SentryRoute>
        <SentryRoute path={['/selectCompany']}>
          <LayoutAuthReverse />
        </SentryRoute>
        <SentryRoute path={['/confirmation', '/changepassword']}>
          <LayoutAuthWide />
        </SentryRoute>
        <SentryRoute
          path={['/company', '/confirmationproject', '/invite', '/stages']}>
          <LayoutAuthWideFooter />
        </SentryRoute>
        <SentryRoute
          path={[
            '/:orgId/project/:projectId/schedule/:scheduleId/lookahead/roadblocks',
            '/:orgId/project/:projectId/schedule/:scheduleId/lookahead',
            '/:orgId/project/:projectId/schedule/:scheduleId/export/weekly',
            '/:orgId/project/:projectId/schedule/:scheduleId'
          ]}>
          <DynamicLayout />
        </SentryRoute>
        <SentryRoute exact path="/projects">
          <LayoutWide />
        </SentryRoute>
        <SentryRoute exact path="/">
          <LayoutAuthReverse />
        </SentryRoute>
        <SentryRoute exact path="/weekreport">
          <Report />
        </SentryRoute>
        <SentryRoute path="/weeklyplan/planning">
          <Redirect to={{ pathname: '/weeklyplan/commintments' }} />
        </SentryRoute>
        <SentryRoute path={partialRouteList}>
          <LayoutRender />
        </SentryRoute>
        <SentryRoute exact path={exactRouteList}>
          <LayoutRender />
        </SentryRoute>
        <SentryRoute path="*">
          <InvalidLinkLayout type={INVALID_LINK} />
        </SentryRoute>
      </Switch>
    </Router>
  );
}

export default withTranslation()(App);
